/**
 * The status vocabularies a batch carries, mirrored from the backend.
 *
 * Kept in step with `nucleus-backend/app/helpers/batch_status.js` — these are the
 * exact strings stored in batch_details.status / qc_status / master_status /
 * lc_status and in containers.qc_status, so a value renamed on one side and not
 * the other silently stops matching.
 *
 * They were previously typed as literals at every comparison site, which is how
 * the same `'Under Sampling'` string ended up meaning "a TRF was raised" in one
 * place and "the clearance was approved" in another.
 */
export const BATCH_STATUS = Object.freeze({
  QUARANTINE: 'Quarantine',
  UNDER_SAMPLING: 'Under Sampling',
  UNDER_TEST: 'Under Test',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  PARTIALLY_ID_APPROVED: 'Partially ID Approved',
  EXPIRED: 'Expired',
  ON_HOLD: 'OnHold',
  MODIFICATION_PENDING: 'Modification Pending'
});

/** batch_details.lc_status — the SAMPLING line clearance, not the BMR one. */
export const LC_STATUS = Object.freeze({
  WH_PENDING: 'WH LC Pending',
  QA_PENDING: 'QALC Pending',
  QA_APPROVED: 'QALC Approved'
});

/**
 * A test request's own lifecycle. Mirrors REQUEST_STATUS in
 * `app/helpers/test_request_cycle.js`.
 *
 * There is no 'Sampled' state: sampling moves the batch straight to Under Test,
 * so a request state between the two would be unobservable.
 */
export const REQUEST_STATUS = Object.freeze({
  RAISED: 'Raised',
  UNDER_TEST: 'Under Test',
  COMPLETED: 'Completed',
  ABANDONED: 'Abandoned'
});

/** Terminal request states. Anything else — except NULL — is still open. */
export const CLOSED_REQUEST_STATUSES = Object.freeze([
  REQUEST_STATUS.COMPLETED,
  REQUEST_STATUS.ABANDONED
]);

/**
 * A request is open unless it has reached a terminal state.
 *
 * A NULL request_status means the row predates cycle tracking and is NEVER open.
 * Reading it as open would report almost every batch as blocked, because nothing
 * ever deactivated a TRF when its batch was approved — 10,256 terminal batches in
 * production still carry an active one.
 */
export const isRequestOpen = (request) =>
  !!request?.request_status &&
  !CLOSED_REQUEST_STATUSES.includes(request?.request_status);

export default BATCH_STATUS;
